/* eslint-disable no-nested-ternary */
/* eslint-disable no-negated-condition */
/* eslint-disable max-lines-per-function */
/* eslint-disable func-style */
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonText,
} from "@ionic/react";
import { isUndefined } from "lodash";
import { useContext } from "react";

import { ApiTokenContext } from "@/contexts/api-token/ApiContextWrapper";
import { UserContext } from "@/contexts/user/UserContext";
import { useAirplanes } from "@/hooks/useAirplanes";
import { useSubscriptionFees } from "@/hooks/useSubscriptionFees";
import { getOrganizationManagementPath } from "@/pages/dashboard/routing/routeHelper";
import {
  calculateDaysLeftInTrial,
  createNavigationProps,
  formatCentsToDollars,
} from "@/utils/common";
import { GET_STARTED_MENU_ITEM } from "@/utils/constants/menu";
import { FeatureAccessLevel } from "@/utils/constants/subscriptions";

import { PricingFeatureList } from "./PricingFeatureList";
import {
  BASIC_FEATURES,
  ESSENTIAL_EXTRAS,
  ESSENTIAL_FEATURES,
  PREMIUM_EXTRAS,
} from "./pricingFeatures";

const FALLBACK_PRICES: Record<
    FeatureAccessLevel.TIER_1 | FeatureAccessLevel.TIER_2,
    string
  > = {
    [FeatureAccessLevel.TIER_1]: "$299.00",
    [FeatureAccessLevel.TIER_2]: "$499.00",
  };

function PaidSubscriptionPricing({
  extras,
  inherited,
  inheritedFrom,
  level,
  name,
  variant,
}: {
  extras: readonly string[];
  inherited: readonly string[];
  inheritedFrom: string;
  level: FeatureAccessLevel.TIER_1 | FeatureAccessLevel.TIER_2;
  name: string;
  variant: "essential" | "premium";
}) {
  const { data: subscriptionFees, isError, isLoading } = useSubscriptionFees();

  const { token } = useContext(ApiTokenContext),
    { user } = useContext(UserContext),
    isLoggedIn = Boolean(token);

  const { data: airplanes } = useAirplanes() ?? {};

  const airplaneInTrial = airplanes?.allAirplanes.find(
    (airplane) =>
      airplane.activeFeatureAccessSubscription?.SubscriptionType ===
        FeatureAccessLevel.TRIAL &&
      new Date(airplane.activeFeatureAccessSubscription.EndDateTime) >
        new Date(),
  );

  const isTrial = Boolean(isLoggedIn && airplaneInTrial);
  const daysRemaining =
    isTrial && airplaneInTrial
      ? calculateDaysLeftInTrial(
          airplaneInTrial.activeFeatureAccessSubscription?.EndDateTime ?? "",
        )
      : 0;

  const fee = subscriptionFees?.fees[level];

  // Fallback to default pricing if API fails
  const displayPrice = (() => {
    if (isLoading) {
      return "Loading...";
    }
    if (isError || isUndefined(fee)) {
      return FALLBACK_PRICES[level];
    } // Fallback to known default
    return fee === 0 ? "Free" : formatCentsToDollars(fee);
  })();

  return (
    <IonCard className={`paid ${variant}`}>
      <IonCardHeader>
        <h1 className="ion-no-margin title-price-paid">{name}</h1>
        <h2 className="ion-no-margin pricing-title-bold">
          {displayPrice === "Free" ? (
            "Free"
          ) : (
            <>
              {displayPrice}
              <span className="pricing-period">per airplane / year</span>
            </>
          )}
        </h2>
        <IonText>Yearly Subscription</IonText>
        <ul className="pricing-perks">
          <li>FREE Trial Subscription</li>
          <li>Unlimited Users</li>
        </ul>
      </IonCardHeader>
      <IonCardContent>
        <PricingFeatureList
          extras={extras}
          inherited={inherited}
          inheritedFrom={inheritedFrom}
        />
        <div className="focus">
          <h2 className="ion-no-padding">1 Month Free Trial</h2>
          <IonText>
            {isLoggedIn
              ? `${daysRemaining} day${
                  daysRemaining !== 1 ? "s" : ""
                } remaining. ${
                  daysRemaining > 0
                    ? "Payment will be prompted after your free trial expires"
                    : ""
                }
                `
              : "Payment will be prompted after your free trial expires"}
          </IonText>
          <IonButton
            {...createNavigationProps(
              // eslint-disable-next-line no-negated-condition, no-nested-ternary
              !isLoggedIn
                ? GET_STARTED_MENU_ITEM.linkTo
                : user?.organizationId
                  ? getOrganizationManagementPath(user.organizationId)
                  : "/dashboard",
            )}
          >
            {isLoggedIn
              ? daysRemaining > 0
                ? "Check my subscriptions"
                : "Upgrade"
              : "Try For Free"}
          </IonButton>
        </div>
      </IonCardContent>
    </IonCard>
  );
}

export const EssentialSubscriptionPricing: React.FC = () => (
  <PaidSubscriptionPricing
    extras={ESSENTIAL_EXTRAS}
    inherited={BASIC_FEATURES}
    inheritedFrom="Basic"
    level={FeatureAccessLevel.TIER_1}
    name="Essential"
    variant="essential"
  />
);

export const PremiumSubscriptionPricing: React.FC = () => (
  <PaidSubscriptionPricing
    extras={PREMIUM_EXTRAS}
    inherited={ESSENTIAL_FEATURES}
    inheritedFrom="Essential"
    level={FeatureAccessLevel.TIER_2}
    name="Premium"
    variant="premium"
  />
);
